import CollapsibleSection from '@boViews/shared/CollapsibleSection';
import { faGlobe, faLightbulb, faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import ChoiceCard from '@sharedComponents/ChoiceCard';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import Typography from '@sharedComponents/Typography';
import { useModal } from '@sharedContexts/ModalContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PUBLIC, RESTRICTED, scopeCount } from '../accessRules';
import { releaseKeys, tableRows, targetKey, targetOf, withoutAccessRows } from '../overrideRules';
import OverrideTable from '../OverrideTable';
import OverrideTextModal from '../OverrideTextModal';
import ScopeChangeModal from '../ScopeChangeModal';
import SubTargetsModal from '../SubTargetsModal';
import TargetSearch from '../TargetSearch';
import TransferModal from '../TransferModal';
import WithoutAccessList from '../WithoutAccessList';

const noChange = async () => false;
const noClient = async () => null;
const noAddition = async () => ({ added: [], failed: [] });

/**
 * "Access & overrides" section of the collection page: the scope of the collection, changed right away
 * once the user confirms it in a modal listing the consequences on the `overrides` rows, then the clients
 * of the collection (overridden when public, configured, i.e. authorized, when restricted), the search that
 * adds one, the table of the rows and, when restricted, the block of the rows kept without access.
 * `collection` and the catalogue `languages` feed the modals editing a row; `languages` also feed the badges
 * of the table.
 * `onChangeVisibility(visibility)` applies the confirmed scope and must not reject: the card of that scope
 * is shown as selected until it settles, and no other change, nor any addition or change of a row, can
 * start meanwhile.
 * `searchClients` and `onAddTarget` feed the search (see TargetSearch).
 * `onSaveOverride(row, changes, successText)` saves the row with `changes` applied (see rowPayload), and
 * `onRemoveOverride(row, successText)` deletes it; `successText` is the translated message to show once done.
 * Both must not reject and resolve to whether they succeeded: the buttons of the row stay disabled until then.
 * `loadClient(uuid)` and `onAddTargets(targets, payload)` feed the "Sub-clients and platforms" modal (see
 * SubTargetsModal). `onTransferOverride(row, target, successText)` moves the platform row `row` to the client
 * `target` chosen in the transfer modal (see TransferModal); it must not reject and resolves to whether it
 * succeeded: the rows of the platform and of the client keep their buttons disabled until then. The modals
 * editing a row close themselves once saved.
 */
const AccessSection = ({
    collection,
    visibility,
    overrides = [],
    languages = [],
    onChangeVisibility = async () => {},
    searchClients,
    onAddTarget,
    onSaveOverride = noChange,
    onRemoveOverride = noChange,
    loadClient = noClient,
    onAddTargets = noAddition,
    onTransferOverride = noChange,
}) => {
    const { t } = useTranslation('bo');
    const { actions } = useModal();
    const [pending, setPending] = useState(null);
    // Keys of the targets whose row has a request in flight.
    const [pendingKeys, setPendingKeys] = useState([]);
    const shown = pending ?? visibility;
    const restricted = visibility === RESTRICTED;
    const total = scopeCount(overrides, visibility);

    const select = (target) => {
        if (pending || target === visibility) return;
        actions.open({
            component: ScopeChangeModal,
            title: target === RESTRICTED ? t('Make the collection restricted?') : t('Make the collection public?'),
            size: 'l',
            target,
            overrides,
            onCancel: actions.close,
            onConfirm: () => {
                actions.close();
                setPending(target);
                onChangeVisibility(target).finally(() => setPending(null));
            },
        });
    };

    const nameOf = (row) => targetOf(row).name;

    // Runs `request` on the rows of the targets of `keys`, whose buttons stay disabled until it settles; resolves
    // to its outcome. Each request holds its own occurrence of a key, so that two requests on a row do not release
    // each other.
    const trackKeys = (keys, request) => {
        setPendingKeys((current) => [...current, ...keys]);
        return request().finally(() => setPendingKeys((current) => releaseKeys(current, keys)));
    };
    const track = (row, request) => trackKeys([targetKey(targetOf(row))], request);
    const save = (row, changes, successText) => track(row, () => onSaveOverride(row, changes, successText));
    const remove = (row, successText) => track(row, () => onRemoveOverride(row, successText));

    const toggleVisible = (row) => {
        const name = nameOf(row);
        if (row.visible === false) return save(row, { visible: null }, t('The collection is visible again for {{name}}.', { name }));
        return save(row, { visible: false }, t('The collection is hidden for {{name}}.', { name }));
    };

    // The bin of the table: a public row leaves the list, a restricted one loses its access but keeps its texts.
    const removeFromTable = (row) => {
        const name = nameOf(row);
        if (restricted) return save(row, { visible: null }, t('The access of {{name}} has been removed.', { name }));
        return remove(row, t('{{name}} has been removed from the list.', { name }));
    };

    const editField = (row, field) => {
        const name = nameOf(row);
        const isTitle = field === 'title';
        const successText = isTitle
            ? t('The title of {{name}} has been saved.', { name })
            : t('The description of {{name}} has been saved.', { name });
        actions.open({
            component: OverrideTextModal,
            size: 'l',
            title: isTitle ? t('Title for {{name}}', { name }) : t('Description for {{name}}', { name }),
            field,
            row,
            overrides,
            collection,
            languages,
            onCancel: actions.close,
            onClose: actions.close,
            onSave: (text) => save(row, { [field]: text }, successText),
        });
    };

    const openSubTargets = (row) =>
        actions.open({
            component: SubTargetsModal,
            size: 'l',
            title: t('Sub-clients and platforms of {{name}}', { name: nameOf(row) }),
            row,
            overrides,
            visibility,
            languages,
            loadClient,
            onAddTargets,
            onClose: actions.close,
        });

    // The rows of the platform and of the client receiving its override are both changed by the transfer.
    const openTransfer = (row) => {
        const url = nameOf(row);
        actions.open({
            component: TransferModal,
            size: 'l',
            title: t('Transfer the override of {{name}}', { name: url }),
            row,
            overrides,
            visibility,
            languages,
            onClose: actions.close,
            onTransfer: (target) =>
                trackKeys([targetKey(targetOf(row)), targetKey(target)], () =>
                    onTransferOverride(
                        row,
                        target,
                        t('The override of {{url}} has been transferred to {{client}}.', { url, client: target.name }),
                    ),
                ),
        });
    };

    return (
        <CollapsibleSection title={t('Access & overrides')}>
            <InformationMessage
                icon={faLightbulb}
                text={t('Changes in this section apply immediately, without using "Save modifications".')}
            />
            <Typography seo={3} fontWeight="bold" color="neutral-primary">
                {t('Scope of the collection')}
            </Typography>
            <FlexBox role="radiogroup" aria-label={t('Scope of the collection')} gap="s" flexWrap="wrap">
                <ChoiceCard
                    icon={faGlobe}
                    title={t('Public (default)')}
                    description={t(
                        'The collection is available to all clients, according to the activated packs. The clients listed below have an overridden title, description or visibility.',
                    )}
                    selected={shown === PUBLIC}
                    onSelect={() => select(PUBLIC)}
                />
                <ChoiceCard
                    icon={faLockKeyhole}
                    title={t('Restricted')}
                    description={t(
                        'The collection is visible only to the clients listed below. Their title and description can be overridden.',
                    )}
                    selected={shown === RESTRICTED}
                    onSelect={() => select(RESTRICTED)}
                />
            </FlexBox>
            <Typography seo={3} fontWeight="bold" color="neutral-primary">
                {restricted ? t('Configured clients ({{total}})', { total }) : t('Overridden clients ({{total}})', { total })}
            </Typography>
            <Typography fontSize="s" color="neutral-secondary">
                {restricted
                    ? t(
                          'Only these clients and platforms see the collection, according to their activated packs. Authorizing a client authorizes its platforms and those of its sub-clients, except those that have their own row. Their title and description can then be overridden.',
                      )
                    : t(
                          'A client override applies to all its platforms and to those of its sub-clients, except those that have their own override: the most specific one fully prevails. Without an override, the collection is shown as defined in the Information tab.',
                      )}
            </Typography>
            <TargetSearch
                visibility={visibility}
                overrides={overrides}
                disabled={Boolean(pending)}
                searchClients={searchClients}
                onAdd={onAddTarget}
            />
            <OverrideTable
                visibility={visibility}
                rows={tableRows(overrides, visibility)}
                languages={languages}
                disabled={Boolean(pending)}
                pendingKeys={pendingKeys}
                onEditField={editField}
                onToggleVisible={toggleVisible}
                onRemove={removeFromTable}
                onOpenSubTargets={openSubTargets}
                onOpenTransfer={openTransfer}
            />
            {restricted && (
                <WithoutAccessList
                    rows={withoutAccessRows(overrides, visibility)}
                    languages={languages}
                    disabled={Boolean(pending)}
                    pendingKeys={pendingKeys}
                    onAuthorize={(row) => save(row, { visible: true }, t('{{name}} is now authorized.', { name: nameOf(row) }))}
                    onRemove={(row) => remove(row, t('The override of {{name}} has been deleted.', { name: nameOf(row) }))}
                />
            )}
        </CollapsibleSection>
    );
};

export default AccessSection;
